import React from 'react';
import { Link } from 'react-router-dom';
import ServiceHero from '../../components/services/ServiceHero';
import { Footer } from '../../components/Footer';
import { Check, GraduationCap, Play, Film } from 'lucide-react';

const OnlineCoursePage = () => {

  const packs = [
    {
      tier: '🥉',
      name: 'First Course Pack',
      price: 35000000,
      forWho: 'first launch, short course (5–10 modules)',
      duration: '2 days',
      days: [
        {
          day: 'DAY 1',
          schedule: [
            { time: '9:00–9:30', title: 'Setup & Brief', desc: 'Teleprompter install, full tech test, script review with client' },
            { time: '9:30–13:00', title: 'Filming', desc: 'Modules 1–5' },
            { time: '13:00–14:00', title: 'Lunch break', desc: '' },
            { time: '14:00–17:30', title: 'Filming', desc: 'Modules 6–10' },
            { time: '17:30–18:00', title: 'Day 1 Review', desc: 'Take review, retake list for Day 2' }
          ]
        },
        {
          day: 'DAY 2',
          schedule: [
            { time: '9:00–11:00', title: 'Retakes', desc: 'Redo modules per Day 1 review' },
            { time: '11:00–12:00', title: 'Intro & Outro', desc: 'Course introduction and conclusion filming' },
            { time: '12:00–13:00', title: 'Lunch break', desc: '' },
            { time: '13:00–15:00', title: 'Export & Organization', desc: 'Files organized by module, HD export' },
            { time: '15:00', title: 'End', desc: 'Raw organized files delivered' }
          ]
        }
      ],
      deliverables: ['Up to 10 edited modules (basic: cuts, titles)', 'Presentation slides integrated on screen', 'Course intro and outro', 'HD files organized by module', 'Delivery within 5 days']
    },
    {
      tier: '🥈',
      name: 'Pro Course Pack',
      price: 65000000,
      forWho: 'complete course (10–30 modules)',
      duration: '3 days',
      popular: true,
      days: [
        {
          day: 'DAY 1 — Setup & Production',
          schedule: [
            { time: '8:30–9:00', title: 'Premium Setup', desc: '3 cameras, 4 mics, teleprompter, cinema lighting' },
            { time: '9:00–9:30', title: 'Technical Brief', desc: 'Script review, camera & sound adjustments, first takes test' },
            { time: '9:30–13:00', title: 'Filming', desc: 'Modules 1–8' },
            { time: '13:00–14:00', title: 'Lunch break', desc: '' },
            { time: '14:00–18:00', title: 'Filming', desc: 'Modules 9–15' }
          ]
        },
        {
          day: 'DAY 2 — Production Continued',
          schedule: [
            { time: '9:00–13:00', title: 'Filming', desc: 'Modules 16–25' },
            { time: '13:00–14:00', title: 'Lunch break', desc: '' },
            { time: '14:00–17:00', title: 'Filming', desc: 'Modules 26–30' },
            { time: '17:00–18:00', title: 'Trailer Filming', desc: '60s trailer to sell the course' }
          ]
        },
        {
          day: 'DAY 3 — Finishing & Export',
          schedule: [
            { time: '9:00–11:00', title: 'Retakes', desc: 'Necessary redos from reviews' },
            { time: '11:00–13:00', title: 'Branded Intro/Outro', desc: 'Branding elements (intro, outro, bumpers)' },
            { time: '13:00–14:00', title: 'Lunch break', desc: '' },
            { time: '14:00–16:00', title: 'Export & Organization', desc: 'Full file organization, thumbnails per module' },
            { time: '16:00', title: 'End', desc: 'HD files delivered' }
          ]
        }
      ],
      deliverables: ['Up to 30 edited modules (animations, lower thirds, music)', 'Branded intro and outro', 'Thumbnail for each module', '60s sales trailer', 'Delivery within 7 days']
    },
    {
      tier: '🥇',
      name: 'Course Empire Pack',
      price: 110000000,
      forWho: 'premium course, signature program, major launch',
      duration: '4 days',
      days: [
        {
          day: 'DAY 1 — Shooting & Brief',
          schedule: [
            { time: '8:00', title: 'VIP Welcome', desc: 'Premium setup, full briefing' },
            { time: '8:30–12:00', title: 'Pro Photo Shooting', desc: 'Visuals for sales page and social media' },
            { time: '12:00–13:00', title: 'Lunch break', desc: '' },
            { time: '13:00–18:00', title: 'Filming', desc: 'First modules' }
          ]
        },
        {
          day: 'DAYS 2 & 3 — Intensive Production',
          schedule: [
            { time: '9:00–13:00', title: 'Filming', desc: 'Continued modules' },
            { time: '13:00–14:00', title: 'Lunch break', desc: '' },
            { time: '14:00–18:00', title: 'Filming', desc: 'Continuation and completion of modules' }
          ]
        },
        {
          day: 'DAY 4 — Marketing & Launch Content',
          schedule: [
            { time: '9:00–11:00', title: 'VSL Filming', desc: 'Video Sales Letter (3–5 min)' },
            { time: '11:00–13:00', title: 'Reels Filming', desc: '5 reels for course launch' },
            { time: '13:00–14:00', title: 'Lunch break', desc: '' },
            { time: '14:00–16:00', title: 'Retakes & Finishing', desc: '' },
            { time: '16:00–17:00', title: 'Final Export', desc: 'Complete file organization' },
            { time: '17:00–18:00', title: 'VIP Review', desc: 'Final review, Google Drive delivery' }
          ]
        }
      ],
      deliverables: ['Unlimited modules — premium cinematic editing', 'Pro photo shoot — 20 retouched photos', 'VSL edited (3–5 min)', '5 launch reels', '2 min sales trailer', 'Google Drive delivery within 10 days']
    }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24">
      <ServiceHero
        tag="ONLINE COURSE FILMING"
        title="Course Production"
        description="High-quality course content filmed in our professional studio with expert production support."
        bookLink="/services/online-course-filming/book"
        bookLabel="Book Course Filming"
        heroImage="https://images.unsplash.com/photo-1633595699310-1e6a0f2e5c10?w=1600&q=80"
        metaTitle="Online Course Filming | Canggu Podcast Studio"
        metaDescription="Professional course video production in Canggu, Bali. Multi-camera filming, editing and delivery."
      />

      {/* Packs */}
      {packs.map((pack, packIndex) => (
        <section key={packIndex} className={`py-16 ${packIndex % 2 === 0 ? 'bg-[#0A0A0A]' : ''}`}>
          <div className="max-w-6xl mx-auto px-6">
            <div className={`border ${pack.popular ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-[#262626] bg-[#121212]'} p-6 md:p-10 relative`}>
              {pack.popular && (
                <span className="absolute -top-3 left-8 px-4 py-1 bg-[#F5A623] text-black text-sm font-semibold">
                  MOST POPULAR
                </span>
              )}
              
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{pack.tier}</span>
                    <h2 className="text-3xl font-heading font-semibold text-white">{pack.name}</h2>
                  </div>
                  <p className="text-gray-400">For: {pack.forWho} — {pack.duration}</p>
                </div>
                <div className="mt-4 md:mt-0 text-right">
                  <div className="text-4xl font-heading font-bold text-[#F5A623]">
                    Rp {(pack.price / 1000000).toFixed(0)}M
                  </div>
                </div>
              </div>

              {/* Days */}
              <div className="space-y-6 mb-8">
                {pack.days.map((day, dayIndex) => (
                  <div key={dayIndex}>
                    <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                      <Film className="w-5 h-5 text-[#F5A623]" />
                      {day.day}
                    </h3>
                    <div className="space-y-2">
                      {day.schedule.map((item, i) => (
                        <div key={i} className="flex gap-4 p-3 bg-[#0D0D0D] border border-[#262626]">
                          <span className="text-[#F5A623] font-mono text-sm w-28 flex-shrink-0">{item.time}</span>
                          <div className="flex-1">
                            <span className="text-white font-medium">{item.title}</span>
                            {item.desc && <span className="text-gray-400 ml-2">— {item.desc}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Deliverables */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4">Deliverables</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {pack.deliverables.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-[#0D0D0D] border border-[#F5A623]/30">
                      <Check className="w-5 h-5 text-[#F5A623] mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
            Ready to Film Your Course?
          </h2>
          <p className="text-gray-400 mb-8">
            Turn your expertise into a professional online course.
          </p>
          <Link
            to="/services/online-course-filming/book"
            className="inline-block px-12 py-4 bg-[#F5A623] text-black font-semibold text-lg hover:bg-[#FFB84D] transition-colors"
            data-testid="book-now-cta"
          >
            Book Course Filming
          </Link>
        </div>
      </section>

      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default OnlineCoursePage;
